/**
 * @suite/tour — provider and public hook.
 *
 * The provider owns the small state machine (idle → active) plus the current
 * config, labels and the narrow-viewport flag, and mounts the overlay beside
 * the host's tree (the overlay portals itself to <body>). Hosts never render
 * tour internals: they call `useFirstRunTour(config)` for the one-time
 * auto-start and `useTour().start(config)` for replay entries.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { TourConfig, TourLabels, TourStep } from "./types";
import { completeTour, dismissTour } from "./tourStorage";
import { isEmbeddedSurface } from "./surface";
import { subscribeTourReplay } from "./replay";
import { TourOverlay } from "./TourOverlay";

type TourPhase = "idle" | "active";

/** How `useFirstRunTour` hands its live config to the replay relay. */
interface ReplayRegistration {
  getConfig: () => TourConfig;
  getLabels: () => TourLabels | undefined;
}

interface TourContextValue {
  phase: TourPhase;
  config: TourConfig | null;
  labels: TourLabels;
  /** Viewports below the breakpoint get centered cards only — no spotlight. */
  narrow: boolean;
  stepIndex: number;
  start: (config: TourConfig, labels?: TourLabels) => void;
  next: () => void;
  back: () => void;
  skip: () => void;
  goTo: (index: number) => void;
  /** Register this document's tour for cross-window replay requests. */
  registerReplay: (appId: string, registration: ReplayRegistration) => () => void;
  /**
   * Host-provided extra content for a step card (rendered after the body).
   * The engine stays domain-free: it renders whatever the host returns —
   * e.g. the hub renders its telemetry-consent row on the closing step —
   * and returns null for steps that need nothing.
   */
  renderStepExtra?: (step: TourStep) => ReactNode;
}

const TourContext = createContext<TourContextValue | null>(null);

const NARROW_QUERY = "(max-width: 767px)";

export function TourProvider({
  children,
  labels: defaultLabels,
  renderStepExtra,
}: {
  children: ReactNode;
  labels?: TourLabels;
  renderStepExtra?: (step: TourStep) => ReactNode;
}) {
  const [phase, setPhase] = useState<TourPhase>("idle");
  const [config, setConfig] = useState<TourConfig | null>(null);
  const [labels, setLabels] = useState<TourLabels>(defaultLabels ?? {});
  const [stepIndex, setStepIndex] = useState(0);
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(NARROW_QUERY).matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const query = window.matchMedia(NARROW_QUERY);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const start = useCallback((nextConfig: TourConfig, nextLabels?: TourLabels) => {
    if (!nextConfig.steps.length) return;
    setConfig(nextConfig);
    setLabels(nextLabels ?? {});
    setStepIndex(0);
    setPhase("active");
  }, []);

  const stop = useCallback(() => {
    setPhase("idle");
    setConfig(null);
    setStepIndex(0);
  }, []);

  // Replay relay: a separate settings window asks the application's MAIN
  // window to replay its tour (the overlay targets live there). The host
  // registers its config through `useFirstRunTour`; embedded panes never
  // answer — the tour is a main-application surface.
  const replayRegistry = useRef(new Map<string, ReplayRegistration>());
  const registerReplay = useCallback((appId: string, registration: ReplayRegistration) => {
    replayRegistry.current.set(appId, registration);
    return () => {
      const current = replayRegistry.current.get(appId);
      if (current === registration) replayRegistry.current.delete(appId);
    };
  }, []);

  useEffect(() => {
    if (isEmbeddedSurface()) return undefined;
    return subscribeTourReplay((appId) => {
      const registration = replayRegistry.current.get(appId);
      if (!registration) return;
      const config = registration.getConfig();
      if (config.steps.length) start(config, registration.getLabels());
    });
  }, [start]);

  /** Advance; finishing the last step completes (persists) and closes. */
  const next = useCallback(() => {
    if (!config) return;
    if (stepIndex >= config.steps.length - 1) {
      completeTour(config.appId, config.version);
      stop();
      return;
    }
    setStepIndex(stepIndex + 1);
  }, [config, stepIndex, stop]);

  const back = useCallback(() => {
    setStepIndex((current) => Math.max(0, current - 1));
  }, []);

  const goTo = useCallback(
    (index: number) => {
      if (!config) return;
      setStepIndex(Math.min(Math.max(0, index), config.steps.length - 1));
    },
    [config]
  );

  const skip = useCallback(() => {
    if (config) dismissTour(config.appId, config.version);
    stop();
  }, [config, stop]);

  const value = useMemo<TourContextValue>(
    () => ({ phase, config, labels, narrow, stepIndex, start, next, back, skip, goTo, registerReplay, renderStepExtra }),
    [phase, config, labels, narrow, stepIndex, start, next, back, skip, goTo, registerReplay, renderStepExtra]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
      <TourOverlay />
    </TourContext.Provider>
  );
}

export function useTourOptional(): TourContextValue | null {
  return useContext(TourContext);
}

export function useTour(): TourContextValue {
  const value = useTourOptional();
  if (!value) throw new Error("useTour must be used inside <TourProvider>.");
  return value;
}
